import styled from 'styled-components';
import { v2Colors, v2FontFamily } from '../formInputsV2/styles/tokens';

// Bootstrap's lg breakpoint (max-width: 991px) — not v2Breakpoints.tablet
// (900px). The rest of the dashboard chrome (Header's hamburger visibility,
// DashboardTheme.styles.js's .page-content width) already switches at lg;
// using a different cutoff here left a 900-991px gap where page-content
// assumed "mobile, full width" while the rail still rendered as a fixed
// desktop panel on top of it.
const mobileBreakpoint = '991px';

// Single sidebar for both desktop and mobile: a persistent, collapsible rail
// at >=tablet widths, and a slide-in drawer over a backdrop below it. Same
// markup for both — only the CSS transforms/positions it differently, so
// there's one nav list to keep in sync instead of two.
export const SidebarV2Styles = styled.div`
  font-family: ${v2FontFamily};

  --sidebar-expanded: 260px;
  --sidebar-collapsed: 76px;

  .backdrop {
    display: none;
  }

  .rail {
    position: fixed;
    top: 0;
    left: 0;
    bottom: 0;
    /* Kept below the app's modal/backdrop layers (they run 999+, up into
       the tens of thousands in places) so a modal's own backdrop dims the
       sidebar too instead of the sidebar poking out on top of it. Old
       Sidebar.jsx used 600 for the same reason. */
    z-index: 700;
    display: flex;
    flex-direction: column;
    width: ${(props) => (props.$collapsed ? 'var(--sidebar-collapsed)' : 'var(--sidebar-expanded)')};
    background: #fff;
    box-shadow: 1px 0 0 #eef1f4, 3px 0 16px rgba(16, 24, 40, 0.06);
    transition: width 180ms ease;
    overflow: hidden;
  }

  .rail-header {
    display: flex;
    align-items: center;
    justify-content: ${(props) => (props.$collapsed ? 'center' : 'space-between')};
    gap: 8px;
    /* left inset matches the nav icons' inset below (nav-scroll's 12px +
       nav-list item's 12px padding-left) so the logo lines up with them */
    padding: 18px 16px 18px 24px;
    min-height: 68px;
    flex-shrink: 0;
  }

  .logo-link {
    display: flex;
    align-items: center;
    overflow: hidden;
  }

  .logo-link img {
    height: 35px;
    width: auto;
    flex-shrink: 0;
  }

  .collapse-toggle {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 8px;
    border: 1px solid ${v2Colors.disabledBorder};
    background: ${v2Colors.disabledSurface};
    color: ${v2Colors.textMuted};
    cursor: pointer;
    flex-shrink: 0;

    svg {
      width: 12px;
      height: 12px;
    }

    &:hover {
      background: ${v2Colors.disabledSurface};
      color: ${v2Colors.text};
    }
  }

  .mobile-close {
    display: none;
  }

  .nav-scroll {
    flex: 1;
    overflow-y: auto;
    padding: 4px 12px 16px;
  }

  ul.nav-list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .nav-list a,
  .nav-list .nav-row {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 12px;
    border-radius: 10px;
    color: ${v2Colors.textMuted};
    text-decoration: none;
    font-size: 14.5px;
    font-weight: 500;
    cursor: pointer;
    white-space: nowrap;
  }

  .nav-list a svg,
  .nav-list .nav-row svg {
    flex-shrink: 0;
    width: 18px;
    height: 18px;
  }

  .nav-list a:hover,
  .nav-list .nav-row:hover {
    background: ${v2Colors.disabledSurface};
    color: ${v2Colors.text};
  }

  .nav-list a.active,
  .nav-list .nav-row.active {
    background: ${v2Colors.primarySoft};
    color: ${v2Colors.primary};
    font-weight: 600;
  }

  .nav-label {
    overflow: hidden;
    text-overflow: ellipsis;
    flex: 1;
  }

  .nav-row .chevron {
    flex-shrink: 0;
    margin-left: auto;
  }

  .nav-row .chevron svg {
    width: 11px;
    height: 11px;
  }

  .sub-list {
    list-style: none;
    margin: 2px 0 4px;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .sub-list a {
    padding: 8px 12px 8px 40px;
    font-size: 13.5px;
  }

  .notification-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    min-width: 20px;
    height: 20px;
    padding: 0 6px;
    background: ${v2Colors.danger};
    border-radius: 999px;
    color: #fff;
    font-size: 11px;
    font-weight: 700;
    line-height: 1;
    font-variant-numeric: tabular-nums;
    margin-left: auto;
  }

  /* Collapsed desktop rail: icon-only, labels/badges hidden, tooltip via title attr */
  ${(props) => props.$collapsed && `
    .nav-label, .chevron, .notification-badge, .sub-list {
      display: none;
    }
    .nav-list a, .nav-list .nav-row {
      justify-content: center;
      padding: 10px;
    }
    .rail-header {
      flex-direction: column;
      gap: 10px;
      padding: 18px 16px;
    }
    .logo-link {
      display: none;
    }
  `}

  @media (max-width: ${mobileBreakpoint}) {
    .collapse-toggle {
      display: none;
    }

    .mobile-close {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 28px;
      height: 28px;
      border-radius: 8px;
      border: none;
      background: transparent;
      color: ${v2Colors.textMuted};
      cursor: pointer;
    }

    .rail {
      width: var(--sidebar-expanded);
      transform: translateX(${(props) => (props.$mobileOpen ? '0' : '-100%')});
      transition: transform 220ms ease;
      box-shadow: 4px 0 24px rgba(16, 24, 40, 0.12);
    }

    .nav-label, .chevron, .sub-list {
      display: revert;
    }

    .notification-badge {
      display: inline-flex;
    }

    .nav-list a, .nav-list .nav-row {
      justify-content: flex-start;
      padding: 10px 12px;
    }

    .rail-header {
      flex-direction: row;
      justify-content: space-between;
    }

    .logo-link {
      display: flex;
    }

    .backdrop {
      display: ${(props) => (props.$mobileOpen ? 'block' : 'none')};
      position: fixed;
      inset: 0;
      z-index: 690;
      background: rgba(16, 24, 40, 0.45);
    }
  }
`;
